"use client";

import { AnimatePresence, motion } from "framer-motion";
import { Archive } from "lucide-react";
import { useTranslations } from "next-intl";
import { useMemo, useState } from "react";

import { EditIngredientModal } from "@/components/ingredients/EditIngredientModal";
import { SwipeableIngredientCard } from "@/components/ingredients/SwipeableIngredientCard";
import { Dialog } from "@/components/shared/Dialog";
import { NomboxLogo } from "@/components/shared/NomboxLogo";
import { Badge } from "@/components/ui/badge";
import type { Ingredient } from "@/lib/types";

interface ArchiveModalProps {
  isOpen: boolean;
  onClose: () => void;
  archivedIngredients: Ingredient[];
  onRestoreIngredient: (id: string) => void;
  onDeleteIngredient: (id: string) => void;
  onUpdateIngredient: (id: string, newText: string) => void;
  onIncrementQuantity?: (id: string) => void;
  onDecrementQuantity?: (id: string) => void;
}

// Helper to format date as MM/DD/YYYY
function formatDate(date: Date): string {
  const d = new Date(date);
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const year = d.getFullYear();
  return `${month}/${day}/${year}`;
}

// Helper to check if a date is today
function isToday(date: Date): boolean {
  const today = new Date();
  const d = new Date(date);
  return (
    d.getDate() === today.getDate() &&
    d.getMonth() === today.getMonth() &&
    d.getFullYear() === today.getFullYear()
  );
}

export function ArchiveModal({
  isOpen,
  onClose,
  archivedIngredients,
  onRestoreIngredient,
  onDeleteIngredient,
  onUpdateIngredient,
  onIncrementQuantity,
  onDecrementQuantity,
}: ArchiveModalProps) {
  const t = useTranslations("ingredients");
  const [editingIngredientId, setEditingIngredientId] = useState<string | null>(null);
  const [editingIngredientText, setEditingIngredientText] = useState("");
  const [editModalIngredientId, setEditModalIngredientId] = useState<string | null>(null);

  // Group ingredients by completion date
  const groupedByDate = useMemo(() => {
    const groups: Record<string, Ingredient[]> = {};

    archivedIngredients.forEach((ing) => {
      if (ing.completedAt) {
        const bucket = isToday(ing.completedAt) ? "today" : formatDate(ing.completedAt);
        if (!groups[bucket]) {
          groups[bucket] = [];
        }
        groups[bucket].push(ing);
      }
    });

    // Sort buckets - "today" first, then by date descending
    const sortedBuckets = Object.keys(groups).sort((a, b) => {
      if (a === "today") return -1;
      if (b === "today") return 1;
      // Parse dates and sort descending
      const dateA = new Date(a);
      const dateB = new Date(b);
      return dateB.getTime() - dateA.getTime();
    });

    return sortedBuckets.map((bucket) => ({
      key: bucket,
      label: bucket === "today" ? t("today") : bucket,
      ingredients: groups[bucket].sort((a, b) => {
        // Within each bucket, sort by completedAt descending (latest first)
        const dateA = a.completedAt ? new Date(a.completedAt).getTime() : 0;
        const dateB = b.completedAt ? new Date(b.completedAt).getTime() : 0;
        return dateB - dateA;
      }),
    }));
  }, [archivedIngredients, t]);

  // Find the current ingredient from the archivedIngredients array by ID
  // This ensures we always have the latest ingredient data
  const editModalIngredient = editModalIngredientId
    ? archivedIngredients.find((ing) => ing.id === editModalIngredientId) || null
    : null;

  const handleStartEdit = (ingredient: Ingredient) => {
    setEditingIngredientId(ingredient.id);
    const fullText = ingredient.quantity
      ? `${ingredient.quantity} ${ingredient.text}`
      : ingredient.text;
    setEditingIngredientText(fullText);
  };

  const handleSaveEdit = () => {
    if (editingIngredientId && editingIngredientText.trim()) {
      onUpdateIngredient(editingIngredientId, editingIngredientText);
      setEditingIngredientId(null);
      setEditingIngredientText("");
    }
  };

  const handleCancelEdit = () => {
    setEditingIngredientId(null);
    setEditingIngredientText("");
  };

  const handleOpenEditModal = (ingredient: Ingredient) => {
    setEditModalIngredientId(ingredient.id);
  };

  const handleCloseEditModal = () => {
    setEditModalIngredientId(null);
  };

  const handleModalSave = (newText: string) => {
    if (editModalIngredient) {
      onUpdateIngredient(editModalIngredient.id, newText);
    }
  };

  const handleModalRestore = () => {
    if (editModalIngredient) {
      onRestoreIngredient(editModalIngredient.id);
    }
  };

  const handleModalDelete = () => {
    if (editModalIngredient) {
      onDeleteIngredient(editModalIngredient.id);
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={
        <>
          {t("archived")}
          <Badge variant="secondary" className="ms-2 align-middle">
            {archivedIngredients.length}
          </Badge>
        </>
      }
    >
      <div className="space-y-6">
        {groupedByDate.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Archive className="w-12 h-12 mx-auto mb-3 opacity-30" strokeWidth={1.5} />
            <p>{t("noArchivedIngredients")}</p>
          </div>
        ) : (
          groupedByDate.map((group) => (
            <div key={group.key} className="space-y-4">
              <h3 className="font-semibold text-lg flex items-center gap-2">{group.label}</h3>
              <div className="space-y-2">
                <AnimatePresence mode="popLayout">
                  {group.ingredients.map((ingredient) => (
                    <motion.div
                      key={ingredient.id}
                      layout
                      initial={{ opacity: 1, scale: 1, height: "auto" }}
                      exit={{
                        opacity: 0,
                        scale: 0.95,
                        height: 0,
                        marginBottom: 0,
                        transition: {
                          opacity: { duration: 0.2, ease: "easeOut" },
                          scale: { duration: 0.2, ease: "easeOut" },
                          height: { duration: 0.25, ease: "easeInOut", delay: 0.1 },
                          marginBottom: { duration: 0.25, ease: "easeInOut", delay: 0.1 },
                        },
                      }}
                      transition={{ layout: { duration: 0.3, ease: "easeInOut" } }}
                    >
                      <SwipeableIngredientCard
                        toggleIcon={NomboxLogo}
                        ingredient={ingredient}
                        isEditing={editingIngredientId === ingredient.id}
                        editText={editingIngredientText}
                        onToggle={() => onRestoreIngredient(ingredient.id)}
                        onDelete={() => onDeleteIngredient(ingredient.id)}
                        onStartEdit={() => handleStartEdit(ingredient)}
                        onSaveEdit={handleSaveEdit}
                        onCancelEdit={handleCancelEdit}
                        onEditTextChange={setEditingIngredientText}
                        onMoreActionsClick={() => handleOpenEditModal(ingredient)}
                      />
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Edit Ingredient Modal */}
      {editModalIngredient && (
        <EditIngredientModal
          isOpen={!!editModalIngredient}
          onClose={handleCloseEditModal}
          ingredient={editModalIngredient}
          onSave={handleModalSave}
          onArchive={handleModalRestore}
          onDelete={handleModalDelete}
          onIncrementQuantity={
            onIncrementQuantity ? () => onIncrementQuantity(editModalIngredient.id) : undefined
          }
          onDecrementQuantity={
            onDecrementQuantity ? () => onDecrementQuantity(editModalIngredient.id) : undefined
          }
          archiveButtonText={t("restore")}
        />
      )}
    </Dialog>
  );
}
